import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Span } from '@sentry/react';
import { endStepSpan, SentryTags, startInteractionTracing } from 'sentry-utilities';
import { SentryIoTransactionDetails } from 'type-declarations';
import { v4 as uuidv4 } from 'uuid';

const initialState: SentryState = {
	stepSpan: null,
	interactionId: null,
};

export interface SentryState {
	stepSpan: Span | null;
	interactionId: string | null;
}

function resetState(state: SentryState): void {
	state.stepSpan = null;
	state.interactionId = null;
}

export const sentrySlice = createSlice({
	name: 'sentry',
	initialState,
	reducers: {
		initializeStepSpan: (state: SentryState, action: PayloadAction<SentryIoTransactionDetails>) => {
			if (state.stepSpan) {
				endStepSpan(state.stepSpan, 'cancelled');
				resetState(state);
			}
			if (!state.interactionId) {
				state.interactionId = uuidv4();
			}
			action.payload.interactionId = state.interactionId;
			state.stepSpan = startInteractionTracing(action.payload);
		},
		endInteractionTracing: (state: SentryState) => {
			//If there is an active stepSpan, it was abandoned.
			if (state.stepSpan) {
				state.stepSpan.setAttribute(SentryTags.USERABANDONED, true);
				endStepSpan(state.stepSpan, 'cancelled');
			}
			resetState(state);
		},
		setAsCouldContinue: (state: SentryState) => {
			if (state.stepSpan) {
				state.stepSpan.setAttribute(SentryTags.COULDCONTINUE, true);
			}
		},
		setAsUserAttempted: (state: SentryState) => {
			if (state.stepSpan) {
				state.stepSpan.setAttribute(SentryTags.USERATTEMPTED, true);
			}
		},
		endCompletedStepSpan: (state: SentryState) => {
			if (state.stepSpan) {
				endStepSpan(state.stepSpan, 'ok');
			}
			//Just set the stepSpan to null. We keep the interaction_id the same here
			//to allow further steps to be tracked under the same ID.
			state.stepSpan = null;
		},
	},
});

export const sentryReducer = sentrySlice.reducer;
export const sentryActions = sentrySlice.actions;
export const {
	initializeStepSpan,
	endInteractionTracing,
	setAsCouldContinue,
	setAsUserAttempted,
	endCompletedStepSpan,
} = sentryActions;
